import {useConfig} from '@aboutyou/config';
import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {
  Price,
  Price_Reduction,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models//price.types';
import {ProductTileV2} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_tile_v2.types';
import {
  OutfitSliderSection,
  OutfitSliderSection_Element,
  OutfitSliderSection_OutfitProduct,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/sections.types';
import {
  PriceTracker,
  PromotionTracker,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/tracker.types';
import {
  OutfitSlider_Element as FeatureOutfitSliderElement,
  OutfitSlider as OutfitSliderRecommendation,
  ProductTiles,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/recommendation.types';
import {TrackingPromotionCreative} from '@aboutyou/legacy-tracking-sdk';
import {useCurrentPageType} from '@aboutyou/router';
import {
  TrackProduct,
  TrackPromotions,
  useTrackOriginClick,
} from '@aboutyou/tracking';
import {
  COLORS,
  FONT_SIZES,
  FONT_WEIGHTS,
  HorizontalSlider,
  HorizontalSliderProps,
  OutfitCollage,
  ProductImage,
  UnstyledLink,
  useGetAriaLabelFromUrl,
  useScrollToSection,
} from '@aboutyou/ui-components';
import {isCaseTypeCheck} from '@aboutyou/utils';
import {styled} from '@linaria/react';
import React, {PropsWithChildren, useMemo, useState} from 'react';
import {ProductOutfitPagination} from '../../backend-services/ArticleDetail/Recommendations/lanes/ProductOutfitLane/ProductOutfitPagination';
import {OutfitCollageFlyout} from '../../backend-services/ArticleDetail/Recommendations/lanes/shared/OutfitCollageFlyout';
import {useOutfitCollageFlyoutOpenedState} from '../../backend-services/ArticleDetail/Recommendations/lanes/shared/utils/useOutfitCollageFlyoutOpenedState';
import {SCROLL_TO_SHOP_THE_LOOK_PARAM} from '../../backend-services/ArticleDetail/ShopTheLookScrollIntoView/useShopTheLookScrollManager';
import {
  IdolImageSizes,
  ProductCollageIdolImage,
} from '../ProductCollageIdolImage/ProductCollageIdolImage';
import {ProductTileTrackedProps} from '../ProductTile/ProductTileTracked';
import {useTileTrackingData} from '../ProductTile/hooks/useTileTrackingData';
import {SizeSelectionOpenedStateKey} from '../SizeSelectionFlyout/hooks/useSizeSelectionOpenedState';

const OutfitSliderFlyoutKey = 'outfitSliderFlyout';

const getSecondaryImagesFromProductTiles = (
  productTiles: ProductTiles,
  streamPosition: number,
  promotionTracker: PromotionTracker,
  getAriaLabelFromUrl: (url: string) => string | undefined,
  openInNewTab: boolean,
) => {
  return productTiles.products.map(tile => {
    // Gallery elements are ordered server-side based on user image preference
    const firstElement = tile.gallery.elements[0];
    const productImage =
      firstElement?.type?.$case === 'image'
        ? firstElement.type.image.image
        : undefined;

    const targetLink = tile.link.url;

    const data = {
      alt: productImage?.description ?? '',
      src: productImage?.src ?? '',
      padded: productImage?.isEnclosed ?? false,
    };

    return {
      data,
      element: (
        <StyledTrackProduct
          productTile={{
            product: tile.productTracker,
            brand: tile.brandTracker,
            price: tile.price,
          }}
          streamPosition={streamPosition}
          key={data.alt}
          trackingData={{
            recoType: promotionTracker.locationId,
            elements: [{id: tile.productId, type: 'product'}],
            promotionId: promotionTracker.promotionId,
            creativeName: promotionTracker.creativeName,
          }}
          badges={[]}
        >
          <StyledUnstyledLink
            to={{
              pathname: targetLink,
              state: {
                addToBasketTrackingData: {
                  linkedContentId: promotionTracker.promotionId,
                  linkedContentType: 'outfit',
                },
              },
            }}
            openLinkInNewTab={openInNewTab}
            aria-label={getAriaLabelFromUrl(targetLink)}
          >
            <ProductImage {...data} />
          </StyledUnstyledLink>
        </StyledTrackProduct>
      ),
    };
  });
};

const calculateReductions = (priceTracker: PriceTracker): Price_Reduction[] => {
  const reductions = [
    priceTracker.campaignReduction,
    priceTracker.couponReduction,
    priceTracker.saleReduction,
    priceTracker.voucherReduction,
  ];

  return reductions
    .filter(reduction => reduction)
    .map(reduction => {
      // we are filter out undefined
      return {
        strikePrice: {
          amount: reduction!,
          currencyCode: priceTracker.currencyCode,
        },
      };
    });
};

const getSecondaryImagesFromProducts = (
  products: OutfitSliderSection_OutfitProduct[],
  streamPosition: number,
  promotionTracker: PromotionTracker,
  openInNewTab: boolean,
  sendTrackingData: (
    trackingData: ProductTileTrackedProps['trackingData'],
    productId?: number,
  ) => void,
) => {
  return products.map(
    ({link, image, productTracker, priceTracker, brandTracker}) => {
      const data = {
        alt: image.description,
        src: image.src,
        padded: image.isEnclosed,
      };

      const trackingPrice = priceTracker
        ? ({
            price: {
              amount: priceTracker.price,
              currencyCode: priceTracker.currencyCode,
            },
            hasDifferentPrices: false,
            reductions: calculateReductions(priceTracker),
            priceTags: calculateReductions(priceTracker).map(reduction => ({
              type: {$case: 'reduction', reduction},
            })),
            tracker: priceTracker,
          } as Price)
        : undefined;

      return {
        element: (
          <StyledTrackProduct
            streamPosition={streamPosition}
            triggerOnce
            productTile={{
              product: productTracker,
              brand: brandTracker,
              price: trackingPrice,
            }}
            trackingData={{
              recoType: promotionTracker.locationId,
              elements: [{id: productTracker.productId, type: 'product'}],
              promotionId: promotionTracker.promotionId,
              creativeName: promotionTracker.creativeName,
            }}
            badges={[]}
          >
            <StyledUnstyledLink
              to={{
                pathname: link.url,
                state: {
                  addToBasketTrackingData: {
                    linkedContentId: promotionTracker.promotionId,
                    linkedContentType: 'outfit',
                  },
                },
              }}
              onClick={() => {
                if (openInNewTab) {
                  sendTrackingData(
                    {recoType: promotionTracker.locationId},
                    productTracker.productId,
                  );
                }
              }}
              openLinkInNewTab={openInNewTab}
              aria-label={undefined}
            >
              <ProductImage {...data} />
            </StyledUnstyledLink>
          </StyledTrackProduct>
        ),
      };
    },
  );
};

const getOutfitSliderData = (
  outfitSliderElement: OutfitSliderSection_Element | FeatureOutfitSliderElement,
  streamPosition: number,
  getAriaLabelFromUrl: (url: string) => string | undefined,
  openInNewTab: boolean,
  sendTrackingData: (
    trackingData: ProductTileTrackedProps['trackingData'],
    productId?: number,
  ) => void,
) => {
  switch (outfitSliderElement.type?.$case) {
    case 'generatedOufit': {
      const {ctaLabel, productTiles, promotionTracker} =
        outfitSliderElement.type.generatedOufit;
      const secondaryImages = getSecondaryImagesFromProductTiles(
        productTiles,
        streamPosition,
        promotionTracker,
        getAriaLabelFromUrl,
        openInNewTab,
      );
      const primaryImage = secondaryImages.shift();
      const [adpTile, ...filteredTiles] = productTiles.products;

      return {
        ctaLabel,
        PrimaryImageContent: null,
        primaryImageLinkTarget: undefined,
        primaryImage: {
          ...primaryImage!.data,
          isEnclosed: primaryImage!.data.padded,
        },
        secondaryImages,
        uniqueKey: promotionTracker.promotionId,
        products: filteredTiles,
        promotionTracker,
        adpTile,
      };
    }
    case 'idol':
    case 'idolOutfit': {
      const idolElement = isCaseTypeCheck(
        'idolOutfit',
        outfitSliderElement.type,
      )
        ? outfitSliderElement.type.idolOutfit
        : outfitSliderElement.type.idol;

      if ('products' in idolElement) {
        const {
          outfitBackground,
          cta,
          profileImage,
          profileName,
          promotionTracker,
          products,
        } = idolElement;

        const secondaryImages = getSecondaryImagesFromProducts(
          products,
          streamPosition,
          promotionTracker,
          openInNewTab,
          sendTrackingData,
        );

        return {
          ctaLabel: cta.label,
          ctaTargetUrl: cta.linkTarget.url,
          PrimaryImageContent: (
            <ProductCollageIdolImage
              profileName={profileName}
              profileImage={profileImage}
              size={IdolImageSizes.small}
            />
          ),
          primaryImageLinkTarget: cta.linkTarget,
          primaryImage: {
            src: outfitBackground.url,
            alt: outfitBackground.description,
            padded: false,
          },
          secondaryImages,
          uniqueKey: promotionTracker.promotionId,
          products: undefined,
          promotionTracker,
          adpTile: undefined,
        };
      } else {
        const {
          outfitBackground,
          cta,
          profileImage,
          profileName,
          promotionTracker,
          productTiles,
        } = idolElement;

        const secondaryImages = getSecondaryImagesFromProductTiles(
          productTiles,
          streamPosition,
          promotionTracker,
          getAriaLabelFromUrl,
          openInNewTab,
        );

        const [adpTile, ...filteredTiles] = productTiles.products;

        return {
          ctaLabel: cta.label,
          ctaTargetUrl: cta.linkTarget.url,
          PrimaryImageContent: (
            <ProductCollageIdolImage
              profileName={profileName}
              profileImage={profileImage}
              size={IdolImageSizes.small}
            />
          ),
          primaryImageLinkTarget: cta.linkTarget,
          primaryImage: {
            src: outfitBackground.url,
            alt: outfitBackground.description,
            padded: false,
          },
          secondaryImages,
          uniqueKey: promotionTracker.promotionId,
          products: filteredTiles,
          promotionTracker,
          adpTile,
        };
      }
    }

    case 'shopTheLookOutfit': {
      const {ctaLabel, productTiles, promotionTracker, image} =
        outfitSliderElement.type.shopTheLookOutfit;
      const secondaryImages = getSecondaryImagesFromProductTiles(
        productTiles,
        streamPosition,
        promotionTracker,
        getAriaLabelFromUrl,
        openInNewTab,
      );

      return {
        ctaLabel,
        PrimaryImageContent: null,
        primaryImageLinkTarget: undefined,
        primaryImage: image,
        secondaryImages,
        uniqueKey: promotionTracker.promotionId,
        products: productTiles.products,
        promotionTracker,
        adpTile: productTiles.products[0],
      };
    }
  }
};

export type productInfoType = {
  productName: string;
  productBrandName: string;
};

interface OutfitSliderProps {
  outfitSliderData: OutfitSliderSection | OutfitSliderRecommendation;
  className?: string;
  streamPosition: number;
  productInfo?: productInfoType;
  showDivider?: boolean;
}

export const OutfitSlider: React.FC<
  OutfitSliderProps & Partial<HorizontalSliderProps>
> = ({
  outfitSliderData,
  className,
  streamPosition,
  productInfo,
  showDivider,
  slideCount = 1,
  hideSideButtons = true,
  ...rest
}) => {
  const getAriaLabelFromUrl = useGetAriaLabelFromUrl();
  const [selectedPromotion, setSelectedPromotion] = useState<{
    primaryImage: any;
    products: ProductTileV2[] | undefined;
    promotionTracker: PromotionTracker | undefined;
    flyoutInfo: {
      headline: string;
      subline: string;
    };
  }>();
  const {headline, subline} = outfitSliderData;

  const outfitRecommendations = useMemo(() => {
    if ('outfitRecommendations' in outfitSliderData) {
      return outfitSliderData.outfitRecommendations;
    }

    if ('elements' in outfitSliderData) {
      return outfitSliderData.elements;
    }

    return [];
  }, [outfitSliderData]);

  const pageType = useCurrentPageType();
  const {openFlyout} = useOutfitCollageFlyoutOpenedState(OutfitSliderFlyoutKey);
  const {ScrollToTarget} = useScrollToSection(SCROLL_TO_SHOP_THE_LOOK_PARAM);
  const {isActive: isOpenInNewTab} = useIsFeatureActive('ab_adp_new_tab');
  const {device} = useConfig();
  const {sendTrackingData} = useTileTrackingData();

  const originClick = useMemo(() => {
    switch (pageType) {
      case 'articleDetail':
        return 'outfit_adp_reco';
      case 'brandShop':
        return 'bas_sponsored_mono_brand_outfits';
      case 'streetwear':
      case 'sportswear':
        return 'outfits';
      default:
        return 'outfit_category_reco';
    }
  }, [pageType]);
  const trackOriginClick = useTrackOriginClick()(originClick);

  return (
    <>
      <ScrollToTarget />
      <StyledHorizontalSlider
        className={className}
        $showDivider={showDivider}
        SlideControlComponentBottom={
          outfitRecommendations.length > 1 ? ProductOutfitPagination : undefined
        }
        uniqueKey="outfit_slider_with_collage"
        title={
          <HeadlineWrapper>
            <span>{subline}</span>
            {headline}
          </HeadlineWrapper>
        }
        slideCount={slideCount}
        hideSideButtons={hideSideButtons}
        {...rest}
      >
        {outfitRecommendations.map((outfitSliderElement, index) => {
          if (!outfitSliderElement.type) {
            return null;
          }
          let onPrimaryCtaClick: VoidFunction | undefined;
          const {
            uniqueKey,
            ctaLabel,
            ctaTargetUrl,
            primaryImage,
            primaryImageLinkTarget,
            PrimaryImageContent,
            secondaryImages,
            products,
            promotionTracker,
            adpTile,
          } = getOutfitSliderData(
            outfitSliderElement,
            streamPosition,
            getAriaLabelFromUrl,
            device === 'desktop' && isOpenInNewTab,
            sendTrackingData,
          )!;

          if (
            outfitSliderElement.type.$case === 'generatedOufit' ||
            outfitSliderElement.type.$case === 'shopTheLookOutfit'
          ) {
            onPrimaryCtaClick = () => {
              if (
                !adpTile &&
                outfitSliderElement.type?.$case === 'generatedOufit'
              ) {
                console.warn(
                  'OutfitSlider: adpTile is undefined for generated outfit, flyout info will be incomplete',
                );
              }
              setSelectedPromotion({
                primaryImage,
                products,
                promotionTracker,
                flyoutInfo: {
                  subline:
                    outfitSliderElement.type?.$case === 'shopTheLookOutfit'
                      ? productInfo!.productBrandName
                      : adpTile?.brandName ?? '',
                  headline:
                    outfitSliderElement.type?.$case === 'shopTheLookOutfit'
                      ? productInfo!.productName
                      : adpTile?.productName ?? '',
                },
              });
              openFlyout();
            };
          }

          const PromotionTracker = ({children}: PropsWithChildren) => {
            const pageType = useCurrentPageType();

            const promotionSource = useMemo(() => {
              switch (pageType) {
                case 'category':
                  return TrackingPromotionCreative.outfitSliderCategory;
                case 'articleDetail':
                  return TrackingPromotionCreative.outfitSliderADP;
                case 'brandShop':
                case 'streetwear':
                case 'sportswear':
                  return TrackingPromotionCreative.OutfitSliderBrand;
              }
            }, [pageType]);

            return (
              <StyledTrackPromotion
                promotion={{
                  source: promotionSource!,
                  promotionTracker: {
                    ...promotionTracker,
                    destination: ctaTargetUrl ?? promotionTracker.destination,
                  },
                }}
                streamPosition={streamPosition}
                triggerOnce
              >
                {children}
              </StyledTrackPromotion>
            );
          };

          const onTrackingClick = () => {
            trackOriginClick();
          };

          return (
            <StyledOutfitCollage
              key={`outfitRecommendation_${uniqueKey}_${index}`}
              ctaLabel={ctaLabel}
              data-testid={`${outfitSliderElement.type.$case}Reco`}
              primaryImage={primaryImage}
              PrimaryImageContent={PrimaryImageContent}
              secondaryImages={secondaryImages}
              primaryImageLinkTarget={primaryImageLinkTarget}
              onClick={onTrackingClick}
              onPrimaryCtaClick={onPrimaryCtaClick}
              PrimaryTracker={
                ['idol', 'idolOutfit'].includes(outfitSliderElement.type?.$case)
                  ? PromotionTracker
                  : undefined
              }
              renderWrapperAs="li"
              ctaVariant={
                ['idol', 'idolOutfit'].includes(outfitSliderElement.type?.$case)
                  ? 'glassy'
                  : undefined
              }
            />
          );
        })}
      </StyledHorizontalSlider>
      {selectedPromotion?.products && selectedPromotion.promotionTracker && (
        <OutfitCollageFlyout
          data-testid={'outfitSliderFlyout'}
          headerContent={{
            image: selectedPromotion?.primaryImage,
            headline: selectedPromotion.flyoutInfo.headline,
            subline: selectedPromotion.flyoutInfo.subline,
          }}
          headline={headline}
          openedStateKey={OutfitSliderFlyoutKey}
          products={selectedPromotion.products}
          sizeSelectionOpenedStateKey={
            SizeSelectionOpenedStateKey.OUTFIT_RECO_SIZE_SELECTION
          }
          trackingData={{
            promotionId: selectedPromotion.promotionTracker.promotionId,
            creativeName: selectedPromotion.promotionTracker.creativeName,
            recoType: selectedPromotion.promotionTracker.promotionName,
            elements: selectedPromotion.products.map(tile => ({
              id: tile.productId,
              type: 'product',
            })),
          }}
          outfitPromotionId={selectedPromotion.promotionTracker.promotionId}
        />
      )}
    </>
  );
};

const StyledOutfitCollage = styled(OutfitCollage)`
  min-height: 400px;
`;

const StyledTrackPromotion = styled(TrackPromotions)`
  display: flex;
  flex-direction: column;
  gap: 28px;
  flex: 2;
  min-height: 400px;
`;

const StyledUnstyledLink = styled(UnstyledLink)`
  display: flex;
  flex: 1;
`;

const StyledTrackProduct = styled(TrackProduct)`
  display: flex;
  flex: 1;
`;

const StyledHorizontalSlider = styled(HorizontalSlider)<{
  $showDivider?: boolean;
}>`
  margin-bottom: 1.5rem;
  border-top: ${({$showDivider}) =>
    $showDivider ? `8px solid ${COLORS.Grey4}` : 'none'};
`;

export const HeadlineWrapper = styled.h2`
  display: flex;
  flex-direction: column;
  font-size: ${FONT_SIZES.px16};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  line-height: 1.25;
  color: ${COLORS.Black};
  width: 100%;
  align-items: center;
  gap: 8px;
  margin: 0;
  text-transform: uppercase;

  span {
    font-size: 0.625em;
    line-height: 1.6;
    color: ${COLORS.Grey1};
    letter-spacing: 0.48px;
    text-transform: unset;
  }
`;
